import { useAppSelector, RootState } from '@/redux/store';
import type { Action, Resource } from '@/context/permissions';
import { Types, type Permission } from '@/interfaces/Auth/auth.interface';

/**
 * usePermissions
 * --------------
 * Reads the logged-in user's permission list from Redux and exposes
 * four helper functions: canView / canCreate / canEdit / canDelete.
 *
 * Admin role bypasses ALL permission checks (always returns true).ß
 *
 * Usage:
 *   const { canView, canCreate } = usePermissions();
 *   if (!canView('product')) return <Forbidden />;
 */
export const usePermissions = () => {
    const { user, userDetailsLoading } = useAppSelector((state: RootState) => state.user);
    const permissions: Permission[] = user?.role?.permissions ?? [];

    /** Check if permissions contain a wildcard entry { resource: "*", actions: ["*"] } */
    const hasWildcardPermission = permissions.some((p) => {
        const isAdmin = user?.type === Types.ADMIN;
        // console.log("isAdmin", user);
        return isAdmin;
    });

    /** Admin bypasses every permission check:
     *  - role name is "admin"  OR
     *  - permissions contain { resource: "*", actions: ["*"] }
     */
    const isAdmin = hasWildcardPermission;

    const hasPermission = (resource: Resource, action: Action): boolean => {
        // Admin always has full access
        if (isAdmin) return true;

        // DASHBOARD BYPASS: Allow everyone to see the home page
        if (resource === 'dashboard' && action === 'view') return true;

        if (!permissions.length) return false;

        return permissions.some((p) => {
            // Wildcard resource matches everything
            if (p.resource !== resource && p.resource !== '*') return false;

            let actionList = p.actions as any;
            if (typeof p.actions === 'string') {
                try {
                    actionList = JSON.parse(p.actions);
                } catch {
                    if (p.actions.includes(',')) {
                        actionList = p.actions.split(',').map(a => a.trim());
                    } else {
                        actionList = p.actions;
                    }
                }
            }

            if (Array.isArray(actionList)) {
                // Wildcard action matches everything
                return actionList.includes(action) || actionList.includes('*');
            }
            return actionList === action || actionList === '*';
        });
    };

    return {
        isAdmin,
        isLoading: userDetailsLoading === 'pending' || userDetailsLoading === 'idle',
        hasPermission,
        canView: (resource: Resource) => hasPermission(resource, 'view'),
        canCreate: (resource: Resource) => hasPermission(resource, 'create'),
        canEdit: (resource: Resource) => hasPermission(resource, 'update'),
        canDelete: (resource: Resource) => hasPermission(resource, 'delete'),
        canApprove: (resource: Resource) => hasPermission(resource, 'approve'),
        canReject: (resource: Resource) => hasPermission(resource, 'reject'),
        canBlock: (resource: Resource) => hasPermission(resource, "block"),
        canApprovedReject: (resource: Resource) => hasPermission(resource, "approved-rejected"),
        canAdminSettings: (resource: Resource) => hasPermission(resource, "admin-settings"),
        canStoreSettings: (resource: Resource) => hasPermission(resource, "store-settings"),
        canExport: (resource: Resource) => hasPermission(resource, "export"),
        // canAssignDriver: (resource: Resource) => hasPermission(resource, "assign-driver"),
    };
};
